<template>
  <div>
    <b-card v-if="!isPartnerFormPage">
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2">
        <h2 class="text-blue font-weight-bold mb-2 mb-md-0">
          {{ $t("partners") }}
        </h2>

        <b-button v-if="adminType(['super_admin','admin_type_a'])" variant="primary" class="ml-md-auto" @click="goToPartnerForm">
          <feather-icon icon="PlusIcon" class="mr-50" />
          {{ $t("add_partner") }}
        </b-button>
      </div>

      <Table :items="partners" striped :url="url" responsive hover class="position-relative" show-empty :fields="fields">

        <template #head()="scope">
          <div class="text-nowrap">{{ $t(scope.label) }}</div>
        </template>

        <template #cell(referral_commission)="data">
          <span>
            {{ data.item.referral_commission ? data.item.referral_commission: '0' }} %
          </span>
        </template>

        <template #cell(partner_annual_fee_share_percentage)="data">
          <span>
            {{ data.item.partner_annual_fee_share_percentage ? data.item.partner_annual_fee_share_percentage : '0' }} %
          </span>
        </template>

        <template #cell(member_annual_fee_discount)="data">
          <span>
            {{ data.item.member_annual_fee_discount ? data.item.member_annual_fee_discount : '0' }} %
          </span>
        </template>

        <template #cell(member_annual_fee_discount_duration)="data">
          <span>
            {{ data.item.member_annual_fee_discount_duration ? data.item.member_annual_fee_discount_duration : '0' }}
            <span v-if="data.item.member_annual_fee_discount_duration">
              {{ $t('years') }}
            </span>
          </span>
        </template>

        <template #cell(free_period_months)="data">
          <span>
            {{ data.item.free_period_months ? data.item.free_period_months : '0' }}
            <span v-if="data.item.free_period_months">
              {{ $t('months') }}
            </span>
          </span>
        </template>

        <template #cell(applied_status)="data">
          <b-badge :variant="data.item.applied_at ? 'success' : 'warning'">
            {{ data.item.applied_at ? $t('applied') : $t('pending') }}
          </b-badge>
        </template>
        <template #cell(created_at)="data">
          <div class="created-at">
            <span>{{ formatDateTime(data.item.created_at) }}</span>
          </div>
        </template>
        <template #cell(company_name)="data">
          <p style="" class="badge badge-light-primary" @click="$router.push(`/partner/${data.item.id}`)" role="button">
            {{ data.item.company_name || data.item.contact_email }}
          </p>
        </template>
        <template #cell(actions)="data">
          <div class="actions">
            <b-button v-if="adminType(['super_admin','admin_type_a'])" @click="copyLink(data.item)" v-b-tooltip.hover :title="$t('copy_link')" variant="outline-dark" size="sm"
                      class="mr-1" :id="'copy-button-' + data.item.id"
                      ref="copyButton">
              <feather-icon icon="CopyIcon"/>
              <b-tooltip :target="'copy-button-' + data.item.id" triggers="click"
                         :show.sync="showCopiedTooltip[data.item.id]"
                         placement="top">
                Copied!
              </b-tooltip>
            </b-button>
            <b-button variant="outline-warning" size="sm" class="mr-1"
                      v-b-tooltip.hover :title="$t('edit')" @click="$router.push(`/partner/${data.item.id}`)">
              <feather-icon icon="EditIcon"/>
            </b-button>
          </div>
        </template>
      </Table>
    </b-card>

    <b-card v-else>
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2">
        <h2 class="text-blue font-weight-bold mb-2 mb-md-0">
          {{ $t("create_a_partner_link") }}
        </h2>

        <b-button variant="outline-secondary" class="ml-md-auto" @click="goBack">
          {{ $t("back") }}
        </b-button>
      </div>

      <b-form @submit.prevent="submitCompanyPrivilege">
        <b-alert
            v-if="generatedPrivilege"
            show
            variant="light-success"
            class="mb-3"
        >
          <p class="font-weight-bold mb-1">
            {{ $t('register_link_generated') }}
          </p>
          <b-input-group>
            <b-form-input
                :value="generatedPrivilege.register_link"
                readonly
            />
            <b-input-group-append>
              <b-button
                  variant="outline-primary"
                  @click="copyGeneratedLink"
              >
                {{ copyLinkSuccess ? $t('link_copied') : $t('copy_link') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div class="d-flex flex-column flex-md-row align-items-md-center mt-2">
            <small class="text-muted" v-if="generatedPrivilege.invitation_sent_at">
              {{ $t('invitation_last_sent') }}: {{ formatDateTime(generatedPrivilege.invitation_sent_at) }}
            </small>
            <b-button
                size="sm"
                variant="success"
                class="mt-1 mt-md-0 ml-md-auto"
                v-if="generatedPrivilege.invitation_sent_at"
                :disabled="isSendingInvite || !generatedPrivilege || !!generatedPrivilege.applied_at"
                @click="sendInvitationEmail"
            >
              <b-spinner small class="mr-50" v-if="isSendingInvite" />
              {{ $t('resend_invitation') }}
            </b-button>
          </div>
        </b-alert>

        <!-- 3 Column Layout -->
        <b-row>
          <!-- Column 1: Company Information -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('company_information') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('company_name')"
                label-for="company-name-input"
            >
              <b-form-input
                  id="company-name-input"
                  v-model.trim="companyForm.company_name"
              />
              <small class="text-danger" v-if="companyFormErrors.company_name">
                {{ companyFormErrors.company_name }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('company_email_address')"
                label-for="company-email-input"
            >
              <b-form-input
                  id="company-email-input"
                  type="email"
                  v-model.trim="companyForm.contact_email"
                  required
              />
              <small class="text-danger" v-if="companyFormErrors.contact_email">
                {{ companyFormErrors.contact_email }}
              </small>
            </b-form-group>
          </b-col>

          <!-- Column 2: Partner's Privileges -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('partner_privileges') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('referral_commission')"
                label-for="referral-commission-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="referral-commission-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.referral_commission"
                    required
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.referral_commission">
                {{ companyFormErrors.referral_commission }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('partner_annual_fee_share_percentage')"
                label-for="partner-fee-share-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="partner-fee-share-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.partner_annual_fee_share_percentage"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.partner_annual_fee_share_percentage">
                {{ companyFormErrors.partner_annual_fee_share_percentage }}
              </small>
            </b-form-group>
          </b-col>

          <!-- Column 3: Member's Privileges -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('member_privileges') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('member_annual_fee_discount')"
                label-for="member-discount-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="member-discount-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.member_annual_fee_discount"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.member_annual_fee_discount">
                {{ companyFormErrors.member_annual_fee_discount }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('member_annual_fee_discount_duration')"
                label-for="member-annual-fee-discount-duration-input"
            >
              <b-input-group :append="$t('years')">
                <b-form-input
                    id="member-annual-fee-discount-duration-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    v-model.number="companyForm.member_annual_fee_discount_duration"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.member_annual_fee_discount_duration">
                {{ companyFormErrors.member_annual_fee_discount_duration }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('free_period_months')"
                label-for="free-period-input"
            >
              <b-input-group :append="$t('months')">
                <b-form-input
                    id="free-period-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    v-model.number="companyForm.free_period_months"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.free_period_months">
                {{ companyFormErrors.free_period_months }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <small class="text-danger d-block mb-2" v-if="companyFormErrors.member_privileges">
          {{ companyFormErrors.member_privileges }}
        </small>

        <b-row>
          <b-col cols="12" md="3">
            <b-form-group>
              <b-form-checkbox v-model="companyForm.send_invitation">
                {{ $t('send_privilege_invite') }}
              </b-form-checkbox>
            </b-form-group>
            <b-form-group
                v-if="companyForm.send_invitation"
                :label="$t('email_language')"
                label-for="invitation-language-select"
            >
              <b-form-select
                  id="invitation-language-select"
                  v-model="companyForm.invitation_language"
                  :options="emailLanguageOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-3">
          <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="goBack"
              :disabled="isSavingCompanyPrivilege"
          >
            {{ $t("back") }}
          </b-button>
          <b-button
              variant="primary"
              type="submit"
              :disabled="isSavingCompanyPrivilege"
          >
            <b-spinner small class="mr-50" v-if="isSavingCompanyPrivilege" />
            {{ $t("save") }}
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>
<script src="https://cdnjs.cloudflare.com/ajax/libs/clipboard.js/2.0.8/clipboard.min.js">
</script>

<script>
import {
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BPagination,
  BTr,
  BTh,
  BButton,
  BLink,
} from "bootstrap-vue";
import CustomLoader from "@/components/Common/CustomLoader";
import Table from "@/components/Common/Table2";
import Ripple from "vue-ripple-directive";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ClipboardJS from 'clipboard';

export default {
  components: {
    Table,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BPagination,
    BTr,
    BTh,
    BButton,
    BLink,
    CustomLoader,
  },
  directives: {
    Ripple,
  },
  computed: {
    rows() {
      return this.totalItems;
    },
    isPartnerFormPage() {
      return this.$route.name === 'partners_add';
    },
  },
  data() {
    return {
      showCopiedTooltip: [],
      page: 1,
      totalItems: 0,
      url: '/referral-program/company-privileges/list',
      partners: null,
      fields: [
        {
          key: "company_name",
          label: "company_name",
        },
        {
          key: "contact_email",
          label: "contact_email",
        },
        {
          key: "referral_commission",
          label: "referral_commission",
        },
        {
          key: "partner_annual_fee_share_percentage",
          label: "partner_annual_fee_share_percentage",
        },
        {
          key: "member_annual_fee_discount",
          label: "member_annual_fee_discount",
        },
        {
          key: "member_annual_fee_discount_duration",
          label: "member_annual_fee_discount_duration",
        },
        {
          key: "free_period_months",
          label: "free_period_months",
        },
        {
          key: "applied_status",
          label: "applied_status",
        },
        {
          key: "created_at",
          label: "created_at",
        },
        {
          key: "actions",
          label: "actions",
          align: "end"
        },
      ],
      // Partner form data
      isSavingCompanyPrivilege: false,
      companyForm: {
        company_name: '',
        contact_email: '',
        referral_commission: 20,
        partner_annual_fee_share_percentage: null,
        member_annual_fee_discount: null,
        member_annual_fee_discount_duration: null,
        free_period_months: null,
        send_invitation: true,
        invitation_language: 'de',
      },
      companyFormErrors: {},
      generatedPrivilege: null,
      isSendingInvite: false,
      copyLinkSuccess: false,
      emailLanguageOptions: [
        { value: 'en', text: this.$t('english') },
        { value: 'de', text: this.$t('german') },
      ],
    };
  },
  created() {
    this.getPartners();
  },
  methods: {
    getPartners() {
      this.$http
          .post(`/partners?page=` + this.page)
          .then((res) => {
            if (res) {
              this.partners = res.data.value.data;
              this.totalItems = res.data.value.total;
              this.page = res.data.value.current_page;
            }
          });
    },
    copyToClipboard(text, itemId) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      this.$set(this.showCopiedTooltip, itemId, true);
      setTimeout(() => {
        this.$set(this.showCopiedTooltip, itemId, false);
      }, 1000);
    },
    copyLink(item) {
      const linkToCopy = item.register_link;
      this.copyToClipboard(linkToCopy, item.id);
    },


    // Partner form methods
    goBack() {
      this.$router.back();
    },

    goToPartnerForm() {
      this.$router.push({ name: 'partners_add' });
    },

    validateCompanyForm() {
      const errors = {};

      if (!this.companyForm.contact_email?.trim()) {
        errors.contact_email = this.$t('required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.companyForm.contact_email)) {
        errors.contact_email = this.$t('invalid_email');
      }

      if (this.companyForm.referral_commission === null || this.companyForm.referral_commission === '') {
        errors.referral_commission = this.$t('required');
      }

      if (this.companyForm.partner_annual_fee_share_percentage === null || this.companyForm.partner_annual_fee_share_percentage === '') {
        errors.partner_annual_fee_share_percentage = this.$t('required');
      }

      const hasFreePeriod     = this.companyForm.free_period_months !== null && this.companyForm.free_period_months !== '';
      const hasMemberDiscount = this.companyForm.member_annual_fee_discount !== null && this.companyForm.member_annual_fee_discount !== '';

      if (!hasMemberDiscount && !hasFreePeriod) {
        errors.member_privileges = this.$t('member_privileges_hint');
      }

      return errors;
    },

    async submitCompanyPrivilege() {
      const errors = this.validateCompanyForm();
      this.companyFormErrors = errors;
      if (Object.keys(errors).length) return;

      this.isSavingCompanyPrivilege = true;

      const payload = {
        company_name: this.companyForm.company_name.trim(),
        contact_email: this.companyForm.contact_email.trim(),
        referral_commission: Number(this.companyForm.referral_commission),
        partner_annual_fee_share_percentage: this.companyForm.partner_annual_fee_share_percentage === null || this.companyForm.partner_annual_fee_share_percentage === '' ? null : Number(this.companyForm.partner_annual_fee_share_percentage),
        member_annual_fee_discount: this.companyForm.member_annual_fee_discount === null || this.companyForm.member_annual_fee_discount === '' ? null : Number(this.companyForm.member_annual_fee_discount),
        member_annual_fee_discount_duration: this.companyForm.member_annual_fee_discount_duration === null || this.companyForm.member_annual_fee_discount_duration === '' ? null : Number(this.companyForm.member_annual_fee_discount_duration),
        free_period_months: this.companyForm.free_period_months === null || this.companyForm.free_period_months === '' ? null : Number(this.companyForm.free_period_months),
        send_invitation: this.companyForm.send_invitation,
        invitation_language: this.companyForm.invitation_language,
      }

      try {
        const res = await this.$http.post('referral-program/company-privileges', payload);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('company_privilege_created'),
            icon: "SuccessIcon",
            variant: "success",
          },
        });

        this.generatedPrivilege = res.data.value;
      } catch (error) {
        this.handleCompanyPrivilegeError(error);
      } finally {
        this.isSavingCompanyPrivilege = false;
      }
    },

    handleCompanyPrivilegeError(error) {
      if (error?.response?.data?.errors) {
        const serverErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          [serverErrors[key]] = error.response.data.errors[key];
        });
        this.companyFormErrors = { ...this.companyFormErrors, ...serverErrors };
        return;
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('error'),
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    },

    copyGeneratedLink() {
      if (!this.generatedPrivilege?.register_link) return;
      const textarea = document.createElement('textarea');
      textarea.value = this.generatedPrivilege.register_link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copyLinkSuccess = true;
      setTimeout(() => {
        this.copyLinkSuccess = false;
      }, 2000);
    },

    async sendInvitationEmail() {
      if (!this.generatedPrivilege) return;
      this.isSendingInvite = true;
      try {
        const res = await this.$http.post(`referral-program/company-privileges/${this.generatedPrivilege.id}/send-invite`);
        this.generatedPrivilege = res.data.value;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('invitation_sent'),
            icon: "SuccessIcon",
            variant: "success",
          },
        });
      } catch (error) {
        const message = error?.response?.data?.message || this.$t('error');
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.isSendingInvite = false;
      }
    },

    formatDateTime(value) {
      if (!value) return '';
      return new Date(value).toLocaleString();
    },
  },
};
</script>

<style scoped>
.actions {
  min-width: 120px;
}
.created-at {
  min-width: 160px;
}
</style>
